import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  MapPin,
  CalendarBlank,
  Crown,
  ArrowRight,
  CaretLeft,
  CaretRight,
  Globe,
} from "@phosphor-icons/react";
import { Seo } from "@/components/Seo";
import { Skeleton } from "@/components/ui/skeleton";
import { ShareButtons, showSeoPath } from "@/components/ShareButtons";
import { API } from "@/lib/api";
import axios from "axios";

const fmt = (start, end) => {
  try {
    const s = new Date(start).toLocaleDateString("en-US", { month: "long", day: "numeric" });
    const e = end
      ? new Date(end).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
      : new Date(start).toLocaleDateString("en-US", { year: "numeric" });
    return `${s} – ${e}`;
  } catch {
    return start;
  }
};

export default function ShowPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [show, setShow] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/shows/${id}`)
      .then((r) => setShow(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#0B0D17] px-5 text-center">
        <Seo title="Show not found" />
        <p className="font-display text-2xl font-bold text-white">Show not found</p>
        <button data-testid="showpage-directory-btn" onClick={() => navigate("/")} className="rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-500">
          Back to directory
        </button>
      </div>
    );
  }

  const ended = show?.status === "archived";
  const BASE = (process.env.REACT_APP_PUBLIC_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
  const slug = (s) => (s || "").toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const countryHref = show ? `/api/franchise-expos/${slug(show.country)}` : "/";
  const categoryHref = show ? `/api/franchise-expos/category/${slug(show.category)}` : "/";
  const jsonLd = show && {
    "@context": "https://schema.org",
    "@type": "Event",
    name: show.name,
    startDate: show.start_date,
    endDate: show.end_date || show.start_date,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    description: show.description,
    image: [show.image_url],
    location: {
      "@type": "Place",
      name: show.venue || show.city,
      address: { "@type": "PostalAddress", addressLocality: show.city, addressCountry: show.country },
    },
    ...(show.website
      ? {
          url: `${BASE}/show/${show.id}`,
          organizer: { "@type": "Organization", name: show.name, url: show.website },
          offers: { "@type": "Offer", url: show.website, availability: "https://schema.org/InStock", validFrom: show.start_date },
        }
      : { url: `${BASE}/show/${show.id}` }),
  };
  const breadcrumbLd = show && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
      { "@type": "ListItem", position: 2, name: show.country, item: `${BASE}${countryHref}` },
      { "@type": "ListItem", position: 3, name: show.category, item: `${BASE}${categoryHref}` },
      { "@type": "ListItem", position: 4, name: show.name, item: `${BASE}/show/${show.id}` },
    ],
  };

  return (
    <div className="min-h-screen bg-[#0B0D17]">
      {show ? (
        <>
          <Seo
            title={`${show.name} — ${show.city}, ${show.country}`}
            description={show.seo_description || `${show.name} (${fmt(show.start_date, show.end_date)}) in ${show.city}, ${show.country}. ${show.description}`}
            image={`${BASE}/api/og/show/${show.id}.png`}
            path={showSeoPath(show)}
          />
          <Helmet>
            <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
          </Helmet>
        </>
      ) : (
        <Seo title="Franchise Show" />
      )}

      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0B0D17]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4 sm:px-8">
          <img src="/wff-logo.png" alt="World Franchise Forum" className="h-9 w-auto cursor-pointer" onClick={() => navigate("/")} />
          <button
            data-testid="showpage-directory-btn"
            onClick={() => navigate("/")}
            className="rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500"
          >
            Browse Directory
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-10 sm:px-8" data-testid="show-page">
        {show ? (
          <nav data-testid="breadcrumbs" className="mb-6 flex flex-wrap items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="transition-colors hover:text-white">Home</Link>
            <CaretRight size={13} className="text-slate-600" />
            <a href={countryHref} data-testid="breadcrumb-country" className="transition-colors hover:text-white">{show.country}</a>
            <CaretRight size={13} className="text-slate-600" />
            <a href={categoryHref} data-testid="breadcrumb-category" className="transition-colors hover:text-white">{show.category}</a>
            <CaretRight size={13} className="text-slate-600" />
            <span className="max-w-[220px] truncate text-slate-200">{show.name}</span>
          </nav>
        ) : (
          <button onClick={() => navigate("/")} className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-white">
            <CaretLeft size={15} /> Back to directory
          </button>
        )}

        {!show ? (
          <Skeleton className="h-[520px] w-full rounded-3xl bg-white/[0.04]" />
        ) : (
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03]"
          >
            <div className="relative h-64 sm:h-80">
              <img src={show.image_url} alt={show.name} className="h-full w-full object-cover opacity-75" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f111c] via-[#0f111c]/40 to-transparent" />
              <div className="absolute bottom-5 left-6 right-6 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white">{show.category}</span>
                {show.is_premium && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/15 px-2.5 py-1 text-xs font-semibold text-amber-300">
                    <Crown size={12} weight="fill" /> Premium
                  </span>
                )}
                {ended && (
                  <span className="rounded-full bg-slate-700/80 px-3 py-1 text-xs font-semibold text-slate-200">Event Ended</span>
                )}
              </div>
            </div>

            <div className="p-7 sm:p-9">
              <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">{show.name}</h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-slate-300">
                <span className="inline-flex items-center gap-2"><MapPin size={17} weight="fill" className="text-blue-400" /> {show.venue ? `${show.venue}, ` : ""}{show.city}, {show.country}</span>
                <span className="inline-flex items-center gap-2"><CalendarBlank size={17} className="text-slate-500" /> {fmt(show.start_date, show.end_date)}</span>
                {show.website && (
                  <a href={show.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-blue-400 hover:underline">
                    <Globe size={17} /> {show.website.replace(/^https?:\/\//, "")}
                  </a>
                )}
              </div>

              <p className="mt-6 text-base leading-relaxed text-slate-300">{show.description}</p>
              <div className="mt-6">
                <ShareButtons show={show} />
              </div>

              <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <p className="text-sm text-slate-300">
                  {ended
                    ? "This event has concluded. Explore upcoming franchise & licensing shows in our live directory."
                    : "Verify your email in the directory to unlock full details, organizer contacts and tickets."}
                </p>
                <button
                  data-testid="showpage-cta"
                  onClick={() => navigate("/")}
                  className="group mt-4 inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-500"
                >
                  Explore the Directory
                  <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>
            </div>
          </motion.article>
        )}
      </main>
    </div>
  );
}
